type ActorBoundaryDisposer = () => void | Promise<void>;

/** Structural QueryClient surface used at the actor boundary (no react-query import). */
export type ActorBoundaryQueryClient = {
  cancelQueries: (
    filters?: undefined,
    options?: { readonly silent?: boolean },
  ) => Promise<unknown>;
  clear: () => void;
};

const actorBoundaryDisposers = new Set<ActorBoundaryDisposer>();

let actorBoundaryController = new AbortController();
let actorBoundaryQueryClient: ActorBoundaryQueryClient | null = null;

export function registerActorBoundaryDisposer(
  disposer: ActorBoundaryDisposer,
): () => void {
  actorBoundaryDisposers.add(disposer);
  return () => {
    actorBoundaryDisposers.delete(disposer);
  };
}

export function setActorBoundaryQueryClient(
  queryClient: ActorBoundaryQueryClient,
): void {
  actorBoundaryQueryClient = queryClient;
}

export function getActorBoundarySignal(): AbortSignal {
  return actorBoundaryController.signal;
}

function composeAbortSignals(
  signals: readonly (AbortSignal | null | undefined)[],
): AbortSignal | undefined {
  const liveSignals = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (liveSignals.length === 0) return undefined;
  if (liveSignals.length === 1) return liveSignals[0];
  const abortSignal = AbortSignal as typeof AbortSignal & {
    any?: (signals: AbortSignal[]) => AbortSignal;
  };
  if (abortSignal.any) return abortSignal.any(liveSignals);

  const controller = new AbortController();
  const abort = () => controller.abort();
  for (const signal of liveSignals) {
    if (signal.aborted) {
      abort();
      break;
    }
    signal.addEventListener("abort", abort, { once: true });
  }
  return controller.signal;
}

export function withActorBoundarySignal<TInit extends RequestInit>(
  init: TInit,
): TInit {
  const signal = composeAbortSignals([init.signal, getActorBoundarySignal()]);
  return signal ? { ...init, signal } : init;
}

function rotateActorBoundarySignal(): void {
  actorBoundaryController.abort();
  actorBoundaryController = new AbortController();
}

export async function disposeActorBoundary(
  queryClient: ActorBoundaryQueryClient = actorBoundaryQueryClient!,
): Promise<void> {
  if (!queryClient) {
    throw new Error("Actor boundary QueryClient is not configured");
  }
  rotateActorBoundarySignal();
  await queryClient.cancelQueries(undefined, { silent: true });
  queryClient.clear();
  for (const disposer of actorBoundaryDisposers) {
    await disposer();
  }
}
